/**
 * Tools store — zoom, undo/redo, clipboard, alignment, flip.
 */
import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import type { DesignItem } from '@core/types';
import { useDesignStore, type ViewName } from './designStore';
import {
  cloneDesignHistoryItems,
  designHistoryKey,
  retainValidHistorySelection,
} from '../utils/designHistory';

const ZOOM_LEVELS = [25, 50, 75, 100, 150, 200, 300] as const;
const DEFAULT_ZOOM_INDEX = 3; // 100%
const HISTORY_DEBOUNCE_MS = 300;
const MAX_HISTORY = 50;

type ItemsSnapshot = Record<ViewName, DesignItem[]>;

export interface DesignerHistorySnapshot {
  version: 2;
  timestamp: number;
  design: {
    items: ItemsSnapshot;
  };
}

export interface HistoryEntry {
  snapshot: DesignerHistorySnapshot;
  timestamp: number;
  reason?: string;
}

export const useToolsStore = defineStore('tools', () => {
  // ── Zoom ────────────────────────────────────────────────
  const zoomIndex = ref(DEFAULT_ZOOM_INDEX);
  const canvasFitScale = ref(1);
  const zoom = computed(() => ZOOM_LEVELS[zoomIndex.value]);
  const zoomScale = computed(() => zoom.value / 100);
  const effectiveZoomScale = computed(() => zoomScale.value * canvasFitScale.value);
  const canZoomIn = computed(() => zoomIndex.value < ZOOM_LEVELS.length - 1);
  const canZoomOut = computed(() => zoomIndex.value > 0);
  const isDefaultZoom = computed(() => zoomIndex.value === DEFAULT_ZOOM_INDEX);

  function zoomIn() {
    if (canZoomIn.value) zoomIndex.value++;
  }
  function zoomOut() {
    if (canZoomOut.value) zoomIndex.value--;
  }
  function zoomReset() {
    zoomIndex.value = DEFAULT_ZOOM_INDEX;
  }
  function setZoom(level: number) {
    const idx = ZOOM_LEVELS.indexOf(level as typeof ZOOM_LEVELS[number]);
    if (idx !== -1) zoomIndex.value = idx;
  }
  function setCanvasFitScale(scale: number) {
    canvasFitScale.value = Number.isFinite(scale) && scale > 0 ? scale : 1;
  }

  // ── Undo / Redo ─────────────────────────────────────────
  const undoStack = ref<HistoryEntry[]>([]);
  const redoStack = ref<HistoryEntry[]>([]);
  const historyReady = ref(false);

  const canUndo = computed(() => undoStack.value.length > 0);
  const canRedo = computed(() => redoStack.value.length > 0);

  let lastSnapshot: DesignerHistorySnapshot | null = null;
  let stopHistoryWatcher: (() => void) | null = null;
  let commitTimer: ReturnType<typeof setTimeout> | null = null;
  let isApplyingHistory = false;
  let historyBatchDepth = 0;
  let pendingBatchCommit = false;

  function cloneSnapshot<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }

  function snapshotKey(snapshot: DesignerHistorySnapshot): string {
    return designHistoryKey(snapshot.design.items);
  }

  function captureSnapshot(): DesignerHistorySnapshot {
    const design = useDesignStore();

    return {
      version: 2,
      timestamp: Date.now(),
      design: {
        items: cloneDesignHistoryItems(design.items as ItemsSnapshot),
      },
    };
  }

  function pushUndoEntry(snapshot: DesignerHistorySnapshot, reason?: string) {
    undoStack.value.push({
      snapshot: cloneSnapshot(snapshot),
      timestamp: Date.now(),
      reason,
    });
    if (undoStack.value.length > MAX_HISTORY) undoStack.value.shift();
  }

  function pushRedoEntry(snapshot: DesignerHistorySnapshot, reason?: string) {
    redoStack.value.push({
      snapshot: cloneSnapshot(snapshot),
      timestamp: Date.now(),
      reason,
    });
    if (redoStack.value.length > MAX_HISTORY) redoStack.value.shift();
  }

  function clearCommitTimer() {
    if (commitTimer) {
      clearTimeout(commitTimer);
      commitTimer = null;
    }
  }

  function commitHistory(reason = 'change') {
    if (!historyReady.value || isApplyingHistory) return;

    const current = captureSnapshot();

    if (!lastSnapshot) {
      lastSnapshot = cloneSnapshot(current);
      return;
    }

    if (snapshotKey(current) === snapshotKey(lastSnapshot)) return;

    pushUndoEntry(lastSnapshot, reason);
    redoStack.value = [];
    lastSnapshot = cloneSnapshot(current);
  }

  function scheduleHistoryCommit(reason = 'change') {
    if (!historyReady.value || isApplyingHistory) return;

    if (historyBatchDepth > 0) {
      pendingBatchCommit = true;
      return;
    }

    clearCommitTimer();
    commitTimer = setTimeout(() => {
      commitTimer = null;
      commitHistory(reason);
    }, HISTORY_DEBOUNCE_MS);
  }

  function flushHistoryCommit(reason = 'change') {
    clearCommitTimer();
    commitHistory(reason);
  }

  function applySnapshot(snapshot: DesignerHistorySnapshot) {
    clearCommitTimer();
    isApplyingHistory = true;

    try {
      const design = useDesignStore();
      const selectedIds = [...design.selectedItemIds];
      design.loadDesignState({
        items: cloneDesignHistoryItems(snapshot.design.items),
      });

      const validSelection = retainValidHistorySelection(selectedIds, design.items[design.activeView] ?? []);
      design.selectItems(validSelection);

      lastSnapshot = cloneSnapshot(snapshot);
    } finally {
      window.setTimeout(() => {
        isApplyingHistory = false;
      }, 0);
    }
  }

  function initHistory() {
    destroyHistory();

    undoStack.value = [];
    redoStack.value = [];
    historyBatchDepth = 0;
    pendingBatchCommit = false;
    isApplyingHistory = false;
    lastSnapshot = captureSnapshot();
    historyReady.value = true;

    const design = useDesignStore();
    stopHistoryWatcher = watch(
      () => design.items,
      () => scheduleHistoryCommit(),
      { deep: true },
    );
  }

  function destroyHistory() {
    clearCommitTimer();
    stopHistoryWatcher?.();
    stopHistoryWatcher = null;
    historyReady.value = false;
  }

  function beginHistoryBatch() {
    if (commitTimer) {
      flushHistoryCommit('before-batch');
    }
    historyBatchDepth++;
  }

  function endHistoryBatch(reason = 'batch') {
    if (historyBatchDepth > 0) {
      historyBatchDepth--;
    }

    if (historyBatchDepth === 0 && pendingBatchCommit) {
      pendingBatchCommit = false;
      flushHistoryCommit(reason);
    } else if (historyBatchDepth === 0) {
      flushHistoryCommit(reason);
    }
  }

  function pushHistory(snapshot?: DesignerHistorySnapshot | string) {
    if (typeof snapshot === 'string') {
      flushHistoryCommit('manual');
      return;
    }

    if (snapshot) {
      pushUndoEntry(snapshot, 'manual');
      redoStack.value = [];
      return;
    }

    flushHistoryCommit('manual');
  }

  function undo(): DesignerHistorySnapshot | null {
    if (!historyReady.value || isApplyingHistory) return null;
    flushHistoryCommit('before-undo');

    const entry = undoStack.value.pop();
    if (!entry) return null;

    const current = captureSnapshot();
    pushRedoEntry(current, 'undo');
    applySnapshot(entry.snapshot);
    return entry.snapshot;
  }

  function redo(): DesignerHistorySnapshot | null {
    if (!historyReady.value || isApplyingHistory) return null;
    flushHistoryCommit('before-redo');

    const entry = redoStack.value.pop();
    if (!entry) return null;

    const current = captureSnapshot();
    pushUndoEntry(current, 'redo');
    applySnapshot(entry.snapshot);
    return entry.snapshot;
  }

  function pushRedo(snapshot?: DesignerHistorySnapshot | string) {
    if (typeof snapshot === 'string') return;

    if (snapshot) {
      pushRedoEntry(snapshot, 'manual');
    }
  }

  // ── Clipboard ───────────────────────────────────────────
  const clipboard = ref<string | null>(null);

  function copy(serializedItem: string) {
    clipboard.value = serializedItem;
  }

  /** Copy an item by ID — the caller passes the serialized item */
  function copyItem(id: string) {
    // Called from useKeyboard; it finds the item from designStore
    // We just store the ID for a simplified paste via pasteItem()
    clipboard.value = id;
  }

  function pasteItem() {
    // pasteItem is a no-op at store level; actual paste is in useKeyboard
    // which has access to designStore without circular dep
  }

  function clearClipboard() {
    clipboard.value = null;
  }

  // ── Full-screen ─────────────────────────────────────────
  const isFullScreen = ref(!!document.fullscreenElement);

  function toggleFullScreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }

  function initFullScreenListener() {
    document.addEventListener('fullscreenchange', () => {
      isFullScreen.value = !!document.fullscreenElement;
    });
  }

  // ── Grid snap ───────────────────────────────────────────
  const gridSnapEnabled = ref(false);
  const GRID_SIZE = 10;

  function toggleGridSnap() {
    gridSnapEnabled.value = !gridSnapEnabled.value;
  }

  function snapToGrid(value: number): number {
    if (!gridSnapEnabled.value) return value;
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
  }

  return {
    // Zoom
    zoom,
    zoomScale,
    effectiveZoomScale,
    canvasFitScale,
    zoomIndex,
    canZoomIn,
    canZoomOut,
    isDefaultZoom,
    zoomIn,
    zoomOut,
    zoomReset,
    setZoom,
    setCanvasFitScale,
    // Undo / Redo
    undoStack,
    redoStack,
    canUndo,
    canRedo,
    historyReady,
    initHistory,
    destroyHistory,
    beginHistoryBatch,
    endHistoryBatch,
    pushHistory,
    undo,
    redo,
    pushRedo,
    // Clipboard
    clipboard,
    copy,
    copyItem,
    pasteItem,
    clearClipboard,
    // Full-screen
    isFullScreen,
    toggleFullScreen,
    initFullScreenListener,
    // Grid snap
    gridSnapEnabled,
    toggleGridSnap,
    snapToGrid,
  };
});
